// Comparison-key normalization and cheap string similarity. Normalization is
// applied only to the *keys* used for equality/similarity — never to the text
// shown to the user, which always stays original.

export function makeNormalizer({ ignoreWhitespace = false, ignoreCase = false } = {}) {
  return (line) => {
    let s = line;
    if (ignoreWhitespace) s = s.trim().replace(/\s+/g, ' ');
    if (ignoreCase) s = s.toLowerCase();
    return s;
  };
}

// Line similarity for move detection — Sørensen–Dice over *tokens*
// (identifiers and numbers), NOT characters. See DEBUGGING.md RCA-001: character
// similarity scores boilerplate-heavy lines like `"1495": {},` and `"1332": {},`
// as ~0.6 similar because they share the scaffolding `": {},`, which fabricates
// moves in JSON/CSV/config data. Token similarity ignores that scaffolding —
// those lines share no tokens (0.0) — while genuinely-edited lines that share
// identifiers (a changed function signature) still score high.
export function lineSimilarity(a, b) {
  if (a === b) return 1;
  const A = tokenize(a);
  const B = tokenize(b);
  // Punctuation-only lines (e.g. `}`) have no tokens: fall back to exact match.
  if (A.size === 0 && B.size === 0) return a.trim() === b.trim() ? 1 : 0;
  if (A.size === 0 || B.size === 0) return 0;
  let intersection = 0;
  for (const t of A) if (B.has(t)) intersection++;
  return (2 * intersection) / (A.size + B.size);
}

/** Whether a line carries any distinctive token (vs. being pure punctuation). */
export function hasToken(s) {
  return tokenize(s).size > 0;
}

/** The identifier/number token set of a string (exported for cheap pre-filters). */
export function tokenSet(s) {
  return tokenize(s);
}

// Tokens are lower-cased so line PAIRING (modify-vs-remove+add, move scoring) is
// case-insensitive: `Beta Gamma` and `beta gamma` share all tokens and pair as a
// modify (with case shown in the inline diff), instead of scoring 0 and splitting
// into a spurious remove + add. Whether case counts as a *change* is decided
// separately by the case-sensitive `norm()` equality, so this doesn't hide edits.
const WORD_TOKEN = /[\p{L}\p{M}\p{N}_]+/gu;
const UNSPACED_SCRIPT = /^[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}\p{M}]+$/u;

function tokenize(s) {
  const tokens = [];
  for (const raw of s.normalize('NFC').match(WORD_TOKEN) || []) {
    const token = raw.toLowerCase();
    const chars = [...token];
    // Scripts commonly written without spaces otherwise become one all-or-nothing
    // token. Content bigrams retain useful overlap across a local edit; restrict
    // this to those scripts so numeric/identifier keys keep RCA-001 distinctiveness.
    if (chars.length > 1 && UNSPACED_SCRIPT.test(token)) {
      for (let i = 0; i < chars.length - 1; i++) tokens.push(chars[i] + chars[i + 1]);
    } else {
      tokens.push(token);
    }
  }
  return new Set(tokens);
}
